import { AfterViewInit, Component, effect, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { IonApp, IonRouterOutlet, LoadingController } from '@ionic/angular/standalone';
import { NavigationEnd, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AdcellService } from 'src/app/services/adcell/adcell.service';
import { AppLanguageDirective } from './directives/app-language.directive';
import { LocaleInvalidateDirective } from './directives/locale-invalidate.directive';
import { CAPACITOR_APP_TOKEN } from './providers/capacitor-app/capacitor-app.provider';
import { SENTRY_SET_USER_TOKEN } from './providers/sentry/sentry.provider';
import { UserLogin } from 'src/app/services/auth/auth.model';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ChatBubbleDragService } from 'src/app/services/chat-bubble-drag.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { MarketingAttributionService } from 'src/app/services/marketing-attribution/marketing-attribution.service';
import { MoEngageService } from 'src/app/services/moengage/moengage.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { PlatformService } from 'src/app/services/platform.service';
import { PushNotificationsService } from 'src/app/services/push-notifications/push-notifications.service';
import { VersionService } from 'src/app/services/version/version.service';
import { environment } from 'src/environments/environment';
import { EventBusService } from './services/eventbus/eventbus.service';
import { FavoritesService } from './services/favorites/favorites.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet, LocaleInvalidateDirective, AppLanguageDirective],
})
export class AppComponent implements AfterViewInit, OnDestroy {
  private readonly navigationService = inject(NavigationService);
  private readonly zone = inject(NgZone);
  private readonly authService = inject(AuthService);
  private readonly chatBubbleDragService = inject(ChatBubbleDragService);
  private readonly router = inject(Router);
  private readonly pushNotificationsService = inject(PushNotificationsService);
  private readonly versionService = inject(VersionService);
  private readonly platformService = inject(PlatformService);
  private readonly moEngageService = inject(MoEngageService);
  private readonly profileService = inject(ProfileService);
  private readonly adcellService = inject(AdcellService);
  private readonly marketingAttributionService = inject(MarketingAttributionService);
  private readonly appPlugin = inject(CAPACITOR_APP_TOKEN);
  private readonly sentrySetUser = inject(SENTRY_SET_USER_TOKEN);
  private readonly logger = LoggerService.create('AppComponent');
  private readonly eventBusService = inject(EventBusService);
  private readonly favoritesService = inject(FavoritesService);
  private readonly loadingController = inject(LoadingController);

  private observer: MutationObserver | null = null;
  private readonly moEngageReady: Promise<void>;
  private resolveMoEngageReady!: () => void;
  private previousUserId: string | null | undefined = undefined;
  private lastProfileSyncAt: number | null = null;

  constructor() {
    this.moEngageReady = new Promise<void>((resolve) => {
      this.resolveMoEngageReady = resolve;
    });

    if (environment.sentryDSN) {
      effect(() => {
        const user = this.authService.user();

        this.sentrySetUser({ email: user?.email ?? 'Guest' });
      });
    }

    effect(() => {
      const user = this.authService.user();

      void this.syncMoEngageUser(user);
    });

    this.init();
    this.registerResumeListener();
    this.trackOnboardingRoute();
    this.trackPageViews();
    this.refreshTokenOnRouteChanges();
    this.adcellService.initFirstPartyTracking();
    this.captureAdcellBid();
    this.captureMarketingAttribution();

    this.events();
  }

  async init() {
    const navigateToUrl = (rawUrl: string) => {
      try {
        const url = new URL(rawUrl);
        const route = url.pathname + (url.search ?? '');

        this.zone.run(() => {
          this.navigationService.navigateByUrl(route || '/');
        });
      } catch {
        this.logger.warn({
          identifier: rawUrl,
          message: 'Failed to parse launch URL',
          method: 'init',
        });
      }
    };

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      navigateToUrl(event.url);
    });

    // Handle Universal Link that launched the app from a killed state.
    // appUrlOpen does not fire in that case — getLaunchUrl captures it instead.
    const launchUrl = await App.getLaunchUrl();

    if (launchUrl?.url) {
      this.logger.info({
        identifier: launchUrl.url,
        message: 'App launched from Universal Link (cold start)',
        method: 'init',
      });

      navigateToUrl(launchUrl.url);
    }

    await this.checkAppVersion();
  }

  private async checkAppVersion(): Promise<void> {
    if (!this.platformService.isNative) {
      return;
    }

    this.logger.info({
      identifier: 'checkAppVersion',
      message: 'Initializing',
    });

    try {
      const [appInfo, minVersion] = await Promise.all([
        this.appPlugin.getInfo(),
        firstValueFrom(this.versionService.fetchMinVersion()),
      ]);

      this.logger.info({
        identifier: 'App.getInfo',
        message: 'Getting App Min Version',
        appInfo: appInfo,
        minVersion: minVersion,
      });

      const minBuild = this.platformService.isIOS ? minVersion.ios : minVersion.android;

      if (parseInt(appInfo.build, 10) < parseInt(minBuild, 10)) {
        await this.router.navigate(['/update-required']);
      }
    } catch (error) {
      this.logger.warn({ identifier: 'checkAppVersion', message: 'Version check failed, proceeding with app', error });
    }
  }

  async ngAfterViewInit() {
    setTimeout(() => {
      this.applyChatWidgetStyles();
      this.chatBubbleDragService.init();
    }, 500);

    this.observer = new MutationObserver(() => {
      this.applyChatWidgetStyles();
      this.chatBubbleDragService.init();
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    await this.moEngageService.initialize(false);
    this.resolveMoEngageReady();

    void this.refreshUserProfile();

    await this.moEngageService.registerForPush();
    await this.moEngageService.setupAndroidPush();

    await this.pushNotificationsService.addListeners();
    this.pushNotificationsService.registerNotifications();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private async syncMoEngageUser(user: UserLogin | null): Promise<void> {
    const currentId = user?.email ?? null;

    if (this.previousUserId === undefined) {
      this.previousUserId = currentId;

      return;
    }

    if (currentId === this.previousUserId) {
      return;
    }

    this.previousUserId = currentId;

    await this.moEngageReady;

    if (!user) {
      await this.moEngageService.resetUser();

      return;
    }

    const profile = await firstValueFrom(this.profileService.loadProfile());

    await this.moEngageService.syncUserProfile(profile);
  }

  private registerResumeListener(): void {
    void this.appPlugin.addListener('resume', () => {
      void this.refreshUserProfile();
    });
  }

  private async refreshUserProfile(): Promise<void> {
    const isAuthenticated = !!this.authService.user();

    this.logger.info({
      identifier: 'refresh_user_profile_invoked',
      message: 'refreshUserProfile invoked',
      method: 'refreshUserProfile',
      isAuthenticated,
    });

    if (!isAuthenticated) {
      return;
    }

    if (!this.shouldRefreshProfile()) {
      return;
    }

    this.lastProfileSyncAt = Date.now();

    await this.moEngageReady;

    try {
      this.logger.info({
        identifier: 'refresh_user_profile_syncing',
        message: 'Syncing MoEngage user profile (refresh path)',
        method: 'refreshUserProfile',
      });

      const profile = await firstValueFrom(this.profileService.loadProfile());

      await this.moEngageService.syncUserProfile(profile, { identify: false });

      this.logger.info({
        identifier: 'refresh_user_profile_completed',
        message: 'MoEngage user profile refresh completed',
        method: 'refreshUserProfile',
      });
    } catch (error) {
      this.logger.error({
        identifier: 'refresh_user_profile',
        message: 'Failed to refresh MoEngage user profile',
        method: 'refreshUserProfile',
        error,
      });
    }
  }

  private shouldRefreshProfile(): boolean {
    if (this.lastProfileSyncAt === null) {
      return true;
    }

    const throttleMs = environment.moEngage.profileRefreshThrottleHours * 60 * 60 * 1000;
    const elapsedMs = Date.now() - this.lastProfileSyncAt;
    const allowed = elapsedMs >= throttleMs;

    const message = allowed
      ? 'Throttle window elapsed — allowing refresh'
      : 'Within throttle window — skipping refresh';

    this.logger.info({
      identifier: 'refresh_user_profile_throttle',
      message,
      method: 'shouldRefreshProfile',
      allowed,
      elapsedMs,
      throttleMs,
    });

    return allowed;
  }

  private trackPageViews(): void {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(() => {
      void this.moEngageReady.then(() => this.moEngageService.trackPageViewed());
    });
  }

  private trackOnboardingRoute(): void {
    if (window.innerWidth >= 768) {
      return;
    }

    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      const onOnboarding = e.urlAfterRedirects.includes('/onboarding');

      this.chatBubbleDragService.setVisibility(!onOnboarding);
    });
  }

  private captureAdcellBid(): void {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      const urlTree = this.router.parseUrl(e.urlAfterRedirects);
      const bid = urlTree.queryParams['bid'] as string | undefined;

      if (!bid) {
        return;
      }

      this.adcellService.captureBid(bid);
    });
  }

  private captureMarketingAttribution(): void {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe((e) => {
      // Read the requested URL, not urlAfterRedirects: Angular rebuilds the query
      // params of a redirect from the redirect target, so the root redirect to
      // /:locale/home drops every tag an ad click arrived with.
      const urlTree = this.router.parseUrl(e.url);

      const captured = this.marketingAttributionService.capture(urlTree.queryParams);

      if (!captured) {
        return;
      }

      this.logger.info({
        identifier: 'marketing_attribution_captured',
        message: 'Marketing attribution captured from landing URL',
        method: 'captureMarketingAttribution',
        utmSource: captured.utmSource,
        utmMedium: captured.utmMedium,
        utmCampaign: captured.utmCampaign,
      });

      const landing = this.marketingAttributionService.takeForLanding(captured);

      if (!landing) {
        return;
      }

      void this.moEngageReady.then(() => this.moEngageService.trackAdLanding(landing));
    });
  }

  private applyChatWidgetStyles(): void {
    const chatWidget = document.querySelector('chat-widget');

    if (!chatWidget) {
      return;
    }

    const shadowRoot = chatWidget.shadowRoot;

    if (!shadowRoot) {
      return;
    }

    const mainContainer = shadowRoot.querySelector('.h-full.w-full.flex.flex-col');

    if (!mainContainer) {
      return;
    }

    (mainContainer as HTMLElement).style.setProperty('max-height', '90%', 'important');
  }

  private events() {
    this.eventBusService.on('logout').subscribe(() => {
      this.favoritesService.clear();
    });

    this.eventBusService.on('loginSuccess').subscribe(() => {
      this.getFavoritesCount();
    });
  }

  private async getFavoritesCount() {
    try {
      await this.favoritesService.getFavoritesCount();
    } catch (error) {
      this.logger.error({
        identifier: 'favorites_count',
        message: 'Failed to get favorites count',
        method: 'getFavoritesCount',
        error,
      });
    }
  }

  private refreshTokenOnRouteChanges(): void {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(async () => {
      const refreshToken = await this.authService.getRefreshToken();

      // This listener also fires while logged out, where a missing or expired
      // refresh token is the normal state — skip silently instead of attempting
      // a refresh that would only fail.
      if (!refreshToken || this.authService.isTokenExpired(this.authService.jwtDecodeToken(refreshToken))) {
        return;
      }

      const isAuthenticated = await this.authService.isAuthenticated();

      // refreshToken() coalesces concurrent callers, so a refresh already in
      // flight is awaited here rather than firing a duplicate request.
      if (!isAuthenticated) {
        const loading = await this.loadingController.create();
        await loading.present();

        try {
          await this.authService.refreshToken();
        } catch (error) {
          this.logger.error({
            identifier: 'app main token refresh',
            message: 'refresh token failure',
            error,
          });
        } finally {
          loading.dismiss();
        }
      }
    });
  }
}
